import React, { useEffect, useState } from 'react';
import { Box, TextField, MenuItem, Select, FormControl, InputLabel, FormHelperText, Autocomplete, Divider } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { IAppointmentRequest, IAppointment } from "@ontime/types";
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { useSelector } from 'react-redux';
import { selectSelectedServiceProvider } from '../../stateManagement/selectedServiceProviderSlice';
import AppointmentService from '../../services/AppointmentService';
import dayjs from 'dayjs';
import { selectOrganizationTasks } from '../../stateManagement/organizationTaskSlice';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import { TimePicker } from '@mui/x-date-pickers';
import { AppointmentStatus } from "@ontime/types";

const appointmentSchema = z.object({
    appointmentId: z.string(),
    version: z.number(),
    // organizationId: z.number().optional(),
    serviceProviderId: z.string(),
    serviceIds: z.string().max(100, "Maximum length of serviceId is 100 characters.").optional(),
    status: z.nativeEnum(AppointmentStatus),
    startDateTime: z.string(),
    endDateTime: z.string().optional(),
    guestFirstName: z.string().min(1, "First Name cannot be empty.").max(50, "Maximum length of First Name is 50 characters."),
    guestLastName: z.string().max(50, "Maximum length of Last Name is 50 characters.").optional(),
    note: z.string().max(200, "Maximum length of Note is 200 characters.").optional(),
});

interface AppointmentFormProps {
    startDateTime: string;
    appointmentInput?: IAppointmentRequest | null;
    closeForm: (input?: IAppointment) => void;
    AllAppointment: IAppointment[] | [];
    loading: boolean;
    setLoading: (input: boolean) => void;
}

const AppointmentGuestForm: React.FC<AppointmentFormProps> = ({
    appointmentInput,
    closeForm,
    startDateTime,
    AllAppointment,
    loading,
    setLoading
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const selectedOrganization = useSelector(selectSelectedOrganization);
    const selectedServiceProvider = useSelector(selectSelectedServiceProvider);

    const organizationTasks = useSelector(selectOrganizationTasks);

    const [minutes, setMinutes] = useState(15);

    const handleDurationClick = (minutesToAdd: number) => {
        const start = dayjs(watch('startDateTime'));
        const end = start.add(minutesToAdd, 'minute');
        setValue('endDateTime', end.format('YYYY-MM-DDTHH:mm:ss.SSS'));
        setMinutes(minutesToAdd);
    };

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control } = useForm<IAppointmentRequest>({
        resolver: zodResolver(appointmentSchema),
        defaultValues: {
            appointmentId: '',
            // organizationId: organization.organizationId,
            serviceProviderId: selectedServiceProvider.serviceProviderId,
            serviceIds: '',
            status: AppointmentStatus.Confirmed,
            startDateTime: startDateTime,
            endDateTime: dayjs(startDateTime).add(minutes, 'minute').format('YYYY-MM-DDTHH:mm:ss.SSS'),
            guestFirstName: '',
            guestLastName: '',
            note: '',
            version: 0,
        },
    });

    useEffect(() => {
        if (appointmentInput) {
            reset({
                appointmentId: appointmentInput.appointmentId,
                // organizationId: organization.organizationId,
                serviceProviderId: appointmentInput.serviceProviderId,
                serviceIds: appointmentInput.serviceIds,
                status: appointmentInput.status as AppointmentStatus,
                startDateTime: appointmentInput.startDateTime,
                endDateTime: appointmentInput.endDateTime,
                guestFirstName: appointmentInput.guestFirstName,
                guestLastName: appointmentInput.guestLastName,
                note: appointmentInput.note,
                version: appointmentInput.version,
            });

            setMinutes(dayjs(appointmentInput.endDateTime).diff(dayjs(appointmentInput.startDateTime), 'minute'))
        }
    }, [appointmentInput, reset]);

    const handleSave = async (data: IAppointmentRequest) => {
        setLoading(true);

        const newStart = dayjs(data.startDateTime);
        const newEnd = dayjs(data.endDateTime);

        const isOverlapping = AllAppointment.filter((item) => item.status !== AppointmentStatus.Canceled).some(app => {
            if (appointmentInput?.appointmentId && app.appointmentId === appointmentInput.appointmentId) return false;

            const existingStart = dayjs(app.startDateTime);
            const existingEnd = dayjs(app.endDateTime);

            return newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart);
        });

        if (isOverlapping) {
            enqueueSnackbar('The selected time overlaps with an existing appointment.', { variant: 'error' });
            setLoading(false);
            return;
        }

        const amount = (data.serviceIds
            ?.split(',')
            .map((id) => {
                const service = organizationTasks.find((os) => os.organizationTaskId === id);
                return service?.price ?? 0;
            }) ?? []).reduce((sum, value) => sum + value, 0);

        const formData = {
            ...data,
            organizationId: selectedOrganization.organizationId,
            amount: amount,
        };

        try {
            if (appointmentInput?.appointmentId) {
                const response = await AppointmentService.Update(selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId, formData);

                closeForm(response);
            } else {
                const response = await AppointmentService.Create(selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId, formData);

                closeForm(response);
            }
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(handleSave)} noValidate>
            <Box display="flex" alignItems="center" gap={2} mt={2}>
                <TextField disabled={loading} fullWidth label="First Name" InputLabelProps={{ shrink: true }} {...register('guestFirstName')} error={!!errors.guestFirstName} helperText={errors.guestFirstName?.message} />
                <TextField disabled={loading} fullWidth label="Last Name" InputLabelProps={{ shrink: true }} {...register('guestLastName')} error={!!errors.guestLastName} helperText={errors.guestLastName?.message} />
            </Box>
            <Box display="flex" alignItems="center" gap={2} mt={2}>
                <Controller
                    name="startDateTime"
                    control={control}
                    render={({ field }) => (
                        <TimePicker
                            label="Start Time"
                            value={dayjs(field.value)}
                            onChange={(newTime) => {
                                const currentDate = dayjs(watch('startDateTime'));
                                const updatedDateTime = currentDate
                                    .hour(newTime?.hour() || 0)
                                    .minute(newTime?.minute() || 0)
                                    .second(0)
                                    .millisecond(0)
                                    .toISOString();

                                field.onChange(updatedDateTime);

                                const newEnd = dayjs(updatedDateTime).add(minutes, 'minute').toISOString();
                                setValue('endDateTime', newEnd);
                            }}
                            slotProps={{ textField: { fullWidth: true } }}
                            minutesStep={15}
                            disabled={loading}
                        />
                    )}
                />
                <FormControl fullWidth error={!!errors.endDateTime} disabled={loading} >
                    <InputLabel>Duration</InputLabel>
                    <Select
                        label="Duration"
                        value={minutes}
                        onChange={(e) => handleDurationClick(Number(e.target.value))}
                    >
                        <MenuItem value='15'> 15M</MenuItem>
                        <MenuItem value='30'> 30M</MenuItem>
                        <MenuItem value='45'> 45M</MenuItem>
                        <MenuItem value='60'> 1H</MenuItem>
                        <Divider />
                        <MenuItem value='75'> 1:15</MenuItem>
                        <MenuItem value='90'> 1:30</MenuItem>
                        <MenuItem value='105'> 1:45</MenuItem>
                        <MenuItem value='120'> 2H</MenuItem>
                        <Divider />
                        <MenuItem value='135'> 2:15</MenuItem>
                        <MenuItem value='150'> 2:30</MenuItem>
                        <MenuItem value='165'> 2:45</MenuItem>
                        <MenuItem value='180'> 3H</MenuItem>
                        <Divider />
                        <MenuItem value='240'> 4H</MenuItem>
                        <MenuItem value='300'> 5H</MenuItem>
                        <MenuItem value='360'> 6H</MenuItem>
                        <MenuItem value='420'> 7H</MenuItem>
                        <MenuItem value='480'> 8H</MenuItem>
                        <MenuItem value='540'> 9H</MenuItem>
                        <MenuItem value='600'> 10H</MenuItem>
                    </Select>
                    <FormHelperText>{errors.endDateTime?.message}</FormHelperText>
                </FormControl>

            </Box>
            <Box display="flex" alignItems="center" gap={2} mt={2}>
                <TextField disabled={loading} fullWidth label="Note" InputLabelProps={{ shrink: true }} multiline rows={3} {...register('note')} error={!!errors.note} helperText={errors.note?.message} />
            </Box>

            <Box display="flex" alignItems="center" gap={2} mt={2}>
                <FormControl fullWidth error={!!errors.status}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        label="Status"
                        value={watch('status')}
                        {...register('status')}
                        onChange={(e) => setValue('status', e.target.value ? (e.target.value as AppointmentStatus) : AppointmentStatus.Confirmed)}
                        disabled={loading}
                    >
                        <MenuItem value={AppointmentStatus.Pending}>Pending</MenuItem>
                        <MenuItem value={AppointmentStatus.Confirmed}>Confirmed</MenuItem>
                        <MenuItem value={AppointmentStatus.Completed}>Completed</MenuItem>
                    </Select>
                    <FormHelperText>{errors.status?.message}</FormHelperText>
                </FormControl>

                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={organizationTasks.filter(s => !(s.isDeleted ?? false))}
                    getOptionLabel={(option) => option.title}
                    value={organizationTasks.filter(service =>
                        watch('serviceIds')?.split(',').map(String).includes(service.organizationTaskId)
                    )}
                    onChange={(_, selectedServices) => {
                        const ids = selectedServices.map(s => s.organizationTaskId).join(',');
                        setValue('serviceIds', ids);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Services"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.serviceIds}
                            helperText={errors.serviceIds?.message}
                        />
                    )}
                    fullWidth
                    disabled={loading}
                />
            </Box>

            <CustomFormAction>
                <CustomButton lable='Save' type='submit' disabled={loading} infoContent={`Save the new appointment record`} />
            </CustomFormAction>
        </form>
    );
};

export default AppointmentGuestForm;